import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import FirmLayout from '@/layouts/FirmLayout'
import AuthLayout from '@/layouts/AuthLayout'
import Login from '@/pages/auth/Login'
import Register from '@/pages/auth/Register'
import FirmDashboard from '@/pages/dashboard/FirmDashboard'
import ClientList from '@/pages/clients/ClientList'
import CreateClient from '@/pages/clients/CreateClient'
import ClientSettings from '@/pages/clients/ClientSettings'
import ChartOfAccounts from '@/pages/ledger/ChartOfAccounts'
import Transactions from '@/pages/ledger/Transactions'
import JournalEntry from '@/pages/ledger/JournalEntry'
import JournalList from '@/pages/ledger/JournalList'
import BankAccounts from '@/pages/banking/BankAccounts'
import ImportStatement from '@/pages/banking/ImportStatement'
import Reconciliation from '@/pages/banking/Reconciliation'
import ReconciliationRules from '@/pages/banking/ReconciliationRules'
import ProfitLoss from '@/pages/reports/ProfitLoss'
import BalanceSheet from '@/pages/reports/BalanceSheet'
import TrialBalance from '@/pages/reports/TrialBalance'
import CashSummary from '@/pages/reports/CashSummary'
import FirmSettings from '@/pages/settings/FirmSettings'
import StaffManagement from '@/pages/settings/StaffManagement'
import ClientDashboard from '@/pages/viewer/ClientDashboard'
import ClientReports from '@/pages/viewer/ClientReports'
import AcceptInvite from '@/pages/auth/AcceptInvite'
import DocumentLibrary from '@/pages/documents/DocumentLibrary'
import BudgetEntry from '@/pages/budgets/BudgetEntry'
import BudgetVariancePage from '@/pages/budgets/BudgetVariance'
import AssetRegister from '@/pages/assets/AssetRegister'
import TrackingCategories from '@/pages/tracking/TrackingCategories'
import BillingPage from '@/pages/billing/BillingPage'
import ActivityFeed from '@/pages/activity/ActivityFeed'
import FirmReports from '@/pages/reports/FirmReports'
import InvoiceList from '@/pages/invoices/InvoiceList'
import CreateInvoice from '@/pages/invoices/CreateInvoice'
import BillList from '@/pages/bills/BillList'
import CreateBill from '@/pages/bills/CreateBill'
import PurchaseOrderList from '@/pages/purchase-orders/PurchaseOrderList'
import CreatePurchaseOrder from '@/pages/purchase-orders/CreatePurchaseOrder'
import QuoteList from '@/pages/quotes/QuoteList'
import CreateQuote from '@/pages/quotes/CreateQuote'
import ContactList from '@/pages/contacts/ContactList'
import CreateContact from '@/pages/contacts/CreateContact'
import ClientOverview from '@/pages/dashboard/ClientOverview'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <Routes>
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
      </Route>

      {/* Protected firm routes */}
      <Route
        element={
          <ProtectedRoute>
            <FirmLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<FirmDashboard />} />
        <Route path="/clients" element={<ClientList />} />
        <Route path="/clients/new" element={<CreateClient />} />
        <Route path="/clients/:clientId/dashboard" element={<ClientOverview />} />
        <Route path="/clients/:clientId/settings" element={<ClientSettings />} />
        <Route path="/clients/:clientId/accounts" element={<ChartOfAccounts />} />
        <Route path="/clients/:clientId/transactions" element={<Transactions />} />
        <Route path="/clients/:clientId/journals" element={<JournalList />} />
        <Route path="/clients/:clientId/journals/new" element={<JournalEntry />} />
        <Route path="/clients/:clientId/banking" element={<BankAccounts />} />
        <Route path="/clients/:clientId/banking/:bankAccountId/import" element={<ImportStatement />} />
        <Route path="/clients/:clientId/banking/:bankAccountId/reconcile" element={<Reconciliation />} />
        <Route path="/clients/:clientId/banking/rules" element={<ReconciliationRules />} />
        <Route path="/clients/:clientId/reports/profit-loss" element={<ProfitLoss />} />
        <Route path="/clients/:clientId/reports/balance-sheet" element={<BalanceSheet />} />
        <Route path="/clients/:clientId/reports/trial-balance" element={<TrialBalance />} />
        <Route path="/clients/:clientId/reports/cash-summary" element={<CashSummary />} />
        <Route path="/clients/:clientId/documents" element={<DocumentLibrary />} />
        <Route path="/clients/:clientId/budgets" element={<BudgetEntry />} />
        <Route path="/clients/:clientId/budgets/variance" element={<BudgetVariancePage />} />
        <Route path="/clients/:clientId/assets" element={<AssetRegister />} />
        <Route path="/clients/:clientId/tracking" element={<TrackingCategories />} />
        <Route path="/clients/:clientId/activity" element={<ActivityFeed />} />
        <Route path="/clients/:clientId/contacts" element={<ContactList />} />
        <Route path="/clients/:clientId/contacts/new" element={<CreateContact />} />
        <Route path="/clients/:clientId/contacts/:contactId/edit" element={<CreateContact />} />
        <Route path="/clients/:clientId/invoices" element={<InvoiceList />} />
        <Route path="/clients/:clientId/invoices/new" element={<CreateInvoice />} />
        <Route path="/clients/:clientId/invoices/:invoiceId/edit" element={<CreateInvoice />} />
        <Route path="/clients/:clientId/bills" element={<BillList />} />
        <Route path="/clients/:clientId/bills/new" element={<CreateBill />} />
        <Route path="/clients/:clientId/bills/:billId/edit" element={<CreateBill />} />
        <Route path="/clients/:clientId/purchase-orders" element={<PurchaseOrderList />} />
        <Route path="/clients/:clientId/purchase-orders/new" element={<CreatePurchaseOrder />} />
        <Route path="/clients/:clientId/purchase-orders/:poId/edit" element={<CreatePurchaseOrder />} />
        <Route path="/clients/:clientId/quotes" element={<QuoteList />} />
        <Route path="/clients/:clientId/quotes/new" element={<CreateQuote />} />
        <Route path="/clients/:clientId/quotes/:quoteId/edit" element={<CreateQuote />} />
        <Route path="/activity" element={<ActivityFeed />} />
        <Route path="/firm-reports" element={<FirmReports />} />
        <Route path="/settings" element={<FirmSettings />} />
        <Route path="/settings/staff" element={<StaffManagement />} />
        <Route path="/settings/billing" element={<BillingPage />} />
      </Route>

      {/* Client viewer portal — requires auth */}
      <Route path="/portal" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
      <Route path="/portal/:clientId/reports" element={<ProtectedRoute><ClientReports /></ProtectedRoute>} />

      {/* Public invite accept page */}
      <Route path="/invite/:token" element={<AcceptInvite />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
